import {
  faDiscord,
  faFacebookMessenger,
  faHubspot,
  faInstagram,
  faIntercom,
  faSlack,
  faTelegram,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import { faComment } from '@fortawesome/free-regular-svg-icons';
import { faCode, faEye, faLink } from '@fortawesome/free-solid-svg-icons';
import { CircleHelp, ThumbsDown, ThumbsUp, type LucideIcon } from 'lucide-react';
import { SiMistralai } from 'react-icons/si';
import i18n from '@/Config/app/i18n';
import { facZendesk } from '@/Config/libs/customIcons';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { ExperimentDto } from '@/Domain/api/Conversation/dto/Experiment.dto';
import { Message } from '@/Domain/api/Conversation/dto/Message.dto';
import { RatingDto, RatingSelectField } from '@/Domain/api/Conversation/dto/Rating.dto';
import { EmbedIcon } from '@/Domain/components/icons/Embed.icon';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { McpClaudeIcon } from '@/Domain/components/icons/McpClaude.icon';
import { McpCursorIcon } from '@/Domain/components/icons/McpCursor.icon';
import { OpenAiIcon } from '@/Domain/components/icons/OpenAi.icon';
import { ConversationExperimentEnum } from '@/Domain/enum/ConversationExperiment.enum';
import {
  ConversationRatingQuestionCodeEnum,
  ConversationRatingResponseCodeEnum,
} from '@/Domain/enum/ConversationRating.enum';
import { ConversationSentimentTypeEnum } from '@/Domain/enum/ConversationSentimentType.enum';
import { ConversationSourceEnum } from '@/Domain/enum/ConversationSource.enum';
import { EventTypeEnum } from '@/Domain/enum/EventType.enum';

// Backend owns the source enum, so it can ship a value this build has never seen.
// These keep such a conversation rendering instead of blanking out.
const UNKNOWN_SOURCE_ICON_COLOR = 'text-muted-foreground';

export const ConversationHelper = {
  humanizeConversationSource: (source: ConversationSourceEnum): string =>
    String(source).replaceAll('_', ' '),

  getConversationIcon: (
    source: Exclude<
      ConversationSourceEnum,
      | ConversationSourceEnum.EMBED
      | ConversationSourceEnum.MCP
      | ConversationSourceEnum.MCP__CLAUDE
      | ConversationSourceEnum.MCP__CURSOR
      | ConversationSourceEnum.MCP__CHATGPT
      | ConversationSourceEnum.MCP__MISTRAL
      | ConversationSourceEnum.SIMULATION
      | ConversationSourceEnum.EMAIL
    >,
  ) => {
    const config = {
      [ConversationSourceEnum.WIDGET]: faComment,
      [ConversationSourceEnum.API]: faCode,
      [ConversationSourceEnum.PREVIEW]: faEye,
      [ConversationSourceEnum.TELEGRAM]: faTelegram,
      [ConversationSourceEnum.SLACK]: faSlack,
      [ConversationSourceEnum.MESSENGER]: faFacebookMessenger,
      [ConversationSourceEnum.DISCORD]: faDiscord,
      [ConversationSourceEnum.INSTAGRAM]: faInstagram,
      [ConversationSourceEnum.WHATSAPP]: faWhatsapp,
      [ConversationSourceEnum.INTERCOM]: faIntercom,
      [ConversationSourceEnum.HUBSPOT]: faHubspot,
      [ConversationSourceEnum.ZENDESK]: facZendesk,
      [ConversationSourceEnum.PUBLIC__LINK]: faLink,
    };

    return config[source];
  },

  customConversationIcons: [
    ConversationSourceEnum.EMBED,
    ConversationSourceEnum.MCP,
    ConversationSourceEnum.MCP__CLAUDE,
    ConversationSourceEnum.MCP__CURSOR,
    ConversationSourceEnum.MCP__CHATGPT,
    ConversationSourceEnum.MCP__MISTRAL,
  ],

  isCustomConversationSource: (
    source: ConversationSourceEnum,
  ): source is
    | ConversationSourceEnum.EMBED
    | ConversationSourceEnum.MCP
    | ConversationSourceEnum.MCP__CLAUDE
    | ConversationSourceEnum.MCP__CURSOR
    | ConversationSourceEnum.MCP__CHATGPT
    | ConversationSourceEnum.MCP__MISTRAL =>
    ConversationHelper.customConversationIcons.includes(source),

  getCustomConversationIcon: (
    source: Extract<
      ConversationSourceEnum,
      | ConversationSourceEnum.EMBED
      | ConversationSourceEnum.MCP__CLAUDE
      | ConversationSourceEnum.MCP
      | ConversationSourceEnum.MCP__CURSOR
      | ConversationSourceEnum.MCP__CHATGPT
      | ConversationSourceEnum.MCP__MISTRAL
    >,
  ) => {
    const config = {
      [ConversationSourceEnum.EMBED]: EmbedIcon,
      [ConversationSourceEnum.MCP]: McpIcon,
      [ConversationSourceEnum.MCP__CLAUDE]: McpClaudeIcon,
      [ConversationSourceEnum.MCP__CURSOR]: McpCursorIcon,
      [ConversationSourceEnum.MCP__CHATGPT]: OpenAiIcon,
      [ConversationSourceEnum.MCP__MISTRAL]: SiMistralai,
    };

    return config[source];
  },

  // Near-black brand marks need a dark: variant, else they sit at ~1:1 on the dark surface.
  getConversationIconColor: (source: ConversationSourceEnum) => {
    const config = {
      [ConversationSourceEnum.WIDGET]: 'text-[#030712] dark:text-foreground',
      [ConversationSourceEnum.API]: 'text-foreground',
      [ConversationSourceEnum.PREVIEW]: 'text-neutral-400',
      [ConversationSourceEnum.TELEGRAM]: 'text-[#35aceb]',
      [ConversationSourceEnum.SLACK]: 'text-[#5a205c] dark:text-[#b07fb2]',
      [ConversationSourceEnum.MESSENGER]: 'text-[#0866ff]',
      [ConversationSourceEnum.DISCORD]: 'text-[#5e6bf3]',
      [ConversationSourceEnum.INSTAGRAM]: 'text-[#E4405F]',
      [ConversationSourceEnum.WHATSAPP]: 'text-[#54c44f]',
      [ConversationSourceEnum.INTERCOM]: 'text-[#091b33] dark:text-[#3b8ff5]',
      [ConversationSourceEnum.HUBSPOT]: 'text-[#ff4702]',
      [ConversationSourceEnum.ZENDESK]: 'text-[#0c3f44] dark:text-[#4aa9b4]',
      [ConversationSourceEnum.PUBLIC__LINK]: 'text-[#030712] dark:text-foreground',
      [ConversationSourceEnum.EMBED]: 'text-[#030712] dark:text-foreground',
      [ConversationSourceEnum.MCP]: 'text-foreground',
      [ConversationSourceEnum.MCP__CLAUDE]: 'text-[#d97856]',
      [ConversationSourceEnum.MCP__CURSOR]: 'text-[#14120b] dark:text-foreground',
      [ConversationSourceEnum.MCP__CHATGPT]: 'text-[#10a37f]',
      [ConversationSourceEnum.MCP__MISTRAL]: 'text-[#fa500f]',
      [ConversationSourceEnum.SIMULATION]: 'text-emerald-500',
      [ConversationSourceEnum.EMAIL]: 'text-foreground',
    };

    return config[source] ?? UNKNOWN_SOURCE_ICON_COLOR;
  },

  getConversationSourceLabel: (source: ConversationSourceEnum): string => {
    const config = {
      [ConversationSourceEnum.WIDGET]: 'Widget',
      [ConversationSourceEnum.API]: 'Api',
      [ConversationSourceEnum.PREVIEW]: 'Preview',
      [ConversationSourceEnum.TELEGRAM]: 'Telegram',
      [ConversationSourceEnum.SLACK]: 'Slack',
      [ConversationSourceEnum.MESSENGER]: 'Messenger',
      [ConversationSourceEnum.DISCORD]: 'Discord',
      [ConversationSourceEnum.INSTAGRAM]: 'Instagram',
      [ConversationSourceEnum.WHATSAPP]: 'Whatsapp',
      [ConversationSourceEnum.INTERCOM]: 'Intercom',
      [ConversationSourceEnum.HUBSPOT]: 'Hubspot',
      [ConversationSourceEnum.ZENDESK]: 'Zendesk',
      [ConversationSourceEnum.PUBLIC__LINK]: 'Chat Page',
      [ConversationSourceEnum.EMBED]: 'Embed',
      [ConversationSourceEnum.MCP]: 'MCP',
      [ConversationSourceEnum.MCP__CLAUDE]: 'MCP Claude',
      [ConversationSourceEnum.MCP__CURSOR]: 'MCP Cursor',
      [ConversationSourceEnum.MCP__CHATGPT]: 'MCP ChatGPT',
      [ConversationSourceEnum.MCP__MISTRAL]: 'MCP Mistral',
      [ConversationSourceEnum.SIMULATION]: 'Simulation',
      [ConversationSourceEnum.EMAIL]: 'Email',
    };

    return config[source] ?? ConversationHelper.humanizeConversationSource(source);
  },

  getExperimentByKey: (
    experiment: Record<ConversationExperimentEnum, ExperimentDto>,
    key: ConversationExperimentEnum,
  ): ExperimentDto<any> | null => (experiment[key] ? experiment[key] : null),

  getSentimentConfig: (sentimentType: ConversationSentimentTypeEnum | null) => {
    const config = {
      [ConversationSentimentTypeEnum.Negative]: {
        icon: '&#x1F610;',
        label: i18n.t('inbox_v2:conversation_details.sentiment.type.negative'),
        description: i18n.t('inbox_v2:conversation_details.sentiment.negative_description'),
        colors: ['#E96363', 'var(--muted)', 'var(--muted)'],
      },
      [ConversationSentimentTypeEnum.Neutral]: {
        icon: '&#x1F642;',
        label: i18n.t('inbox_v2:conversation_details.sentiment.type.neutral'),
        description: i18n.t('inbox_v2:conversation_details.sentiment.neutral_description'),
        colors: ['#B2B6BE', '#B2B6BE', 'var(--muted)'],
      },
      [ConversationSentimentTypeEnum.Positive]: {
        icon: '&#x1F604;',
        label: i18n.t('inbox_v2:conversation_details.sentiment.type.positive'),
        description: i18n.t('inbox_v2:conversation_details.sentiment.positive_description'),
        colors: ['#10B77F', '#10B77F', '#10B77F'],
      },
    };

    if (!sentimentType) {
      return {
        icon: '&#x1F914;',
        label: null,
        description: null,
        colors: ['var(--muted)', 'var(--muted)', 'var(--muted)'],
      };
    }

    return config[sentimentType];
  },

  getPreviousMessage: (events: Event[], index: number): Message | null => {
    const prevEvent = events[index - 1];

    if (prevEvent.event_type === EventTypeEnum.MESSAGE) {
      return prevEvent.data;
    }

    return null;
  },

  hasRating: (rating: RatingDto[] | undefined): boolean => {
    if (!rating) {
      return false;
    }

    return rating.some(
      (ratingItem) => ratingItem.response_code !== ConversationRatingResponseCodeEnum.Unset,
    );
  },

  getRatingByQuestionCode: (
    ratings: RatingDto[],
    questionCode: ConversationRatingQuestionCodeEnum,
  ) => ratings.find((rating) => rating.question_code === questionCode),

  getRatingByResponseCode: (
    responseCode?: ConversationRatingResponseCodeEnum,
  ): LucideIcon | null => {
    const config: Record<ConversationRatingResponseCodeEnum, LucideIcon | null> = {
      [ConversationRatingResponseCodeEnum.VeryPositive]: ThumbsUp,
      [ConversationRatingResponseCodeEnum.Positive]: ThumbsUp,
      [ConversationRatingResponseCodeEnum.Neutral]: CircleHelp,
      [ConversationRatingResponseCodeEnum.Negative]: ThumbsDown,
      [ConversationRatingResponseCodeEnum.VeryNegative]: ThumbsDown,
      [ConversationRatingResponseCodeEnum.Unset]: CircleHelp,
      [ConversationRatingResponseCodeEnum.Other]: null,
    };

    if (!responseCode) {
      return CircleHelp;
    }

    return config[responseCode];
  },

  getRatingSelectedField: (selectedFields: RatingSelectField[], code: string) =>
    selectedFields.find((field) => field.code === code)?.value || '',
};
